'use client';

import { useAui } from '@assistant-ui/react';
import { type JSX, type PropsWithChildren } from 'react';

import { Text } from '@videoblocks/design-system';

function HelperCardTemplate({
    title,
    description,
    children,
}: PropsWithChildren<{
    title: string;
    description?: string;
}>): JSX.Element {
    return (
        <div className="w-[380px] overflow-hidden rounded-2xl bg-white shadow-lg">
            <div className="flex flex-col gap-3 p-5">
                <Text fontSize="lg" fontWeight="semibold">
                    {title}
                </Text>
                {description && (
                    <Text fontSize="sm" fontWeight="regular" className="text-gray-600">
                        {description}
                    </Text>
                )}
                {children}
            </div>
        </div>
    );
}

function HowHelperCard(): JSX.Element {
    return (
        <HelperCardTemplate
            title="How does it work?"
            description="Describe what you need like you'd tell a colleague. You can mention the subject, the feeling you want, technical details, or how you'll use it."
        >
            <Text fontSize="sm" fontWeight="semibold">
                A good prompt might include:
            </Text>
            <ul className="flex flex-col gap-2">
                <li className="flex items-start gap-2">
                    <Text fontSize="sm" fontWeight="semibold">
                        Subject
                    </Text>
                    <Text fontSize="sm" fontWeight="regular" className="text-gray-700">
                        {`— what's in the shot (people hiking, a close-up of coffee being poured)`}
                    </Text>
                </li>
                <li className="flex items-start gap-2">
                    <Text fontSize="sm" fontWeight="semibold">
                        Context
                    </Text>
                    <Text fontSize="sm" fontWeight="regular" className="text-gray-700">
                        — where and when (downtown Tokyo at night, a quiet beach at sunrise)
                    </Text>
                </li>
                <li className="flex items-start gap-2">
                    <Text fontSize="sm" fontWeight="semibold">
                        Mood
                    </Text>
                    <Text fontSize="sm" fontWeight="regular" className="text-gray-700">
                        — how it should feel (energetic, calm, nostalgic, corporate)
                    </Text>
                </li>
            </ul>
        </HelperCardTemplate>
    );
}

function WhyHelperCard(): JSX.Element {
    return (
        <HelperCardTemplate
            title="Why is it faster?"
            description="Once you see results, tell me what's working and what isn't. You don't need to start a new search—just describe what to change."
        >
            <Text fontSize="sm" fontWeight="semibold">
                You might say:
            </Text>
            <div className="flex flex-col gap-3 border-l-2 border-ocean-500 pl-3">
                <Text fontSize="sm" fontWeight="regular" className="italic text-gray-600">
                    {`"These are too corporate, I need something more authentic"`}
                </Text>
                <Text fontSize="sm" fontWeight="regular" className="italic text-gray-600">
                    {`"Same vibe but outdoors instead of indoors"`}
                </Text>
                <Text fontSize="sm" fontWeight="regular" className="italic text-gray-600">
                    {`"Good, but none of them should have people"`}
                </Text>
                <Text fontSize="sm" fontWeight="regular" className="italic text-gray-600">
                    {`"I like the third one—more like that"`}
                </Text>
            </div>
        </HelperCardTemplate>
    );
}

const sampleSearches = [
    {
        title: 'Corporate & professional',
        description:
            '"Diverse team collaborating in a bright modern office, natural lighting, positive energy, no visible logos"',
    },
    {
        title: 'Moody & cinematic',
        description:
            '"Empty city streets at night after rain, neon reflections on wet pavement, slow motion, no people"',
    },
];

function WhatHelperCard(): JSX.Element {
    const aui = useAui();
    const submitSearch = (prompt: string) => {
        // remove leading and trailing quotes, no internal ones
        aui.composer().setText(prompt.replace(/^"(.+)"$/, '$1'));
        aui.composer().send();
    };

    return (
        <HelperCardTemplate title="What can I search for?">
            {sampleSearches.map(({ title, description }) => (
                <div key={title} className="flex flex-col gap-2 bg-gray-50 p-3 rounded-lg">
                    <Text fontSize="sm" fontWeight="semibold">
                        {title}
                    </Text>
                    <Text fontSize="sm" fontWeight="regular" className="italic text-gray-600">
                        {description}
                    </Text>
                    <button
                        type="button"
                        className="flex items-center gap-1 text-sm font-medium text-ocean-500 cursor-pointer hover:underline"
                        onClick={() => submitSearch(description)}
                    >
                        →&nbsp;Try this
                    </button>
                </div>
            ))}
        </HelperCardTemplate>
    );
}

export const CARDS = [
    { id: 'how-helper-card-1', Component: HowHelperCard },
    { id: 'why-helper-card-2', Component: WhyHelperCard },
    { id: 'what-helper-card-3', Component: WhatHelperCard },
];
